// Markdown export for the calculation engine.
//
// The export is intentionally simple — the engine already produces all
// the data the user needs; we just flatten it into the sectioned report
// shape described in docs/UI_FLOW.md §9.

import type { ExcessGfaResult } from "../logic/excess-gfa.js";
import type { TemporaryUseFeeResult } from "../logic/temporary-use.js";
import type { IndustrialCapacityIncreaseResult } from "../types/capacity-increase.js";
import type { KeyIndustrialLeaseAnnualRentResult } from "../types/lease-rent.js";
import type { CalculationResult } from "../types/calculation-result.js";
import type { ChangePremiumResult } from "../types/change-premium.js";
import type { MarketPriceSet } from "../types/market-price.js";
import type { Parcel } from "../types/parcel.js";
import type { ValidationMessage } from "../types/validation.js";

// ---------------------------------------------------------------------------
// Section helpers — each returns a single Markdown section for the
// corresponding slice of the result. The top-level `format*` functions
// compose them into a full report.
// ---------------------------------------------------------------------------

export function formatParcelSection(parcel: Parcel): string {
  const lines: string[] = [
    "## 一、宗地基础信息",
    "",
    "| 字段 | 值 |",
    "|---|---|",
    `| 项目名称 | ${parcel.projectName} |`,
    `| 宗地编号 | ${parcel.parcelId ?? "—"} |`,
    `| 土地面积 (㎡) | ${parcel.landArea} |`,
    `| 供应方式 | ${parcel.supplyMethod} |`,
    `| 测算类型 | ${parcel.calculationType} |`,
    `| 测算时点 | ${parcel.calculationDate} |`,
    `| 主用途 | ${parcel.mainLandUse} |`,
    `| 项目类型 | ${parcel.projectType} |`,
    `| 总建筑面积 (㎡) | ${parcel.totalGfa} |`,
    `| 产权归政府建筑面积 (㎡) | ${parcel.governmentOwnedGfa ?? 0} |`,
  ];
  if (parcel.remark) {
    lines.push(`| 备注 | ${parcel.remark} |`);
  }
  return lines.join("\n");
}

export function formatMarketPriceSection(prices: MarketPriceSet): string {
  const rows: Array<[string, number | undefined]> = [
    ["住宅", prices.residential],
    ["商业", prices.commercial],
    ["办公", prices.office],
    ["工业", prices.industrial],
    ["新型产业 (M0)", prices.m0],
    ["酒店", prices.hotel],
    ["公共管理与服务设施", prices.publicService],
    ["公用设施", prices.utility],
  ];
  const lines: string[] = [
    "## 二、市场价格输入",
    "",
    "| 适用地价类型 | 市场价格 (元/㎡) |",
    "|---|---:|",
  ];
  for (const [name, value] of rows) {
    lines.push(`| ${name} | ${value ?? "—"} |`);
  }
  lines.push(`| 价格来源 | ${prices.priceSource ?? "—"} |`);
  lines.push(`| 价格时点 | ${prices.priceDate ?? "—"} |`);
  return lines.join("\n");
}

/**
 * `市场价格来源说明` — surfaces the official-source metadata fields
 * recorded on the `MarketPriceSet` so the reader can trace each
 * market price back to the user's source.
 *
 * The section is rendered only when at least one of the metadata
 * fields is present. The project itself does not fetch or scrape
 * any official site; the fields are pure record-keeping.
 */
export function formatMarketPriceSourceSection(
  prices: MarketPriceSet,
): string {
  const rows: Array<[string, string | undefined]> = [
    ["官方来源", prices.marketPriceSource],
    ["发布年份", prices.marketPriceYear],
    ["评估日期", prices.valuationDate],
    ["生效日期", prices.effectiveDate],
    ["标准宗地号", prices.standardParcelCode],
    ["区段编号", prices.benchmarkZoneCode],
    ["官方查询 URL", prices.officialQueryUrl],
    ["来源备注", prices.priceSourceRemark],
  ];
  const present = rows.filter(([, v]) => v !== undefined && v !== "");
  if (present.length === 0) return "";
  const lines: string[] = [
    "## 二·B、市场价格来源说明",
    "",
    "> 本节为记录性元数据，不参与测算。项目本身不抓取 / 不调用官方查询网站。",
    "",
    "| 字段 | 值 |",
    "|---|---|",
  ];
  for (const [k, v] of present) {
    lines.push(`| ${k} | ${mdEscape(v as string)} |`);
  }
  return lines.join("\n");
}

function mdEscape(s: string): string {
  // Escape characters that would break Markdown table cells.
  return s.replace(/\|/g, "\\|").replace(/\n/g, " ").replace(/\r/g, " ");
}

export function formatItemResultsSection(
  result: CalculationResult,
  heading = "## 五、分项测算结果",
): string {
  const lines: string[] = [heading, ""];
  if (result.itemResults.length === 0) {
    lines.push("（无建筑面积分项）");
    return lines.join("\n");
  }
  lines.push(
    "| 分项ID | 建筑类型 | 建筑面积 | 计价面积 | 适用价格类型 | 市场价格 | 综合系数 | 分项地价 | 分项楼面地价 |",
    "|---|---|---:|---:|---|---:|---:|---:|---:|",
  );
  for (const r of result.itemResults) {
    lines.push(
      `| ${r.itemId} | ${r.buildingType} | ${r.gfa} | ${r.chargeableGfa} | ` +
        `${r.applicablePriceType} | ${r.marketPrice} | ` +
        `${formatNumber(r.factorChain.finalFactor)} | ` +
        `${formatNumber(r.itemLandPrice)} | ` +
        `${formatNumber(r.floorLandPrice)} |`,
    );
  }
  return lines.join("\n");
}

export function formatFactorChainSection(
  result: CalculationResult,
  heading = "## 四、系数匹配结果",
): string {
  const lines: string[] = [heading, ""];
  if (result.itemResults.length === 0) {
    lines.push("（无建筑面积分项）");
    return lines.join("\n");
  }
  for (const r of result.itemResults) {
    const c = r.factorChain;
    lines.push(
      `### 分项 ${r.itemId}（${r.buildingType}）`,
      "",
      "| 系数 | 值 |",
      "|---|---:|",
      `| 市场价格 | ${c.marketPrice} |`,
      `| 建筑类型修正系数 | ${c.buildingTypeFactor} |`,
      `| 土地使用年期修正系数 | ${formatNumber(c.landTermFactor)} |`,
      `| 产权条件修正系数 | ${c.propertyConditionFactor} |`,
      `| 保障性住房修正系数 | ${c.affordableHousingFactor} |`,
      `| 产业发展导向修正系数 | ${c.industryOrientationFactor} |`,
      `| 产业项目类型修正系数 | ${c.industryProjectTypeFactor} |`,
      `| 地上商业楼层修正系数 | ${c.commercialFloorFactor} |`,
      `| 地下空间修正系数 | ${c.undergroundSpaceFactor} |`,
      `| 项目修正系数 | ${c.projectFactor} |`,
      `| **综合系数** | **${formatNumber(c.finalFactor)}** |`,
    );
    if (c.notes.length > 0) {
      lines.push("", `> 备注：${c.notes.join("；")}`);
    }
    if (r.formulaText) {
      lines.push("", `> 公式：${r.formulaText}`);
    }
    lines.push("");
  }
  return lines.join("\n");
}

export function formatValidationSection(
  messages: ValidationMessage[],
  heading = "## 七、规则校验与人工判断项",
): string {
  const lines: string[] = [heading, ""];
  if (messages.length === 0) {
    lines.push("（无校验消息）");
    return lines.join("\n");
  }
  lines.push("| 级别 | 字段 | 消息 | 建议 |", "|---|---|---|---|");
  for (const m of messages) {
    const levelLabel = m.level === "error" ? "❌ 错误" : m.level === "warning" ? "⚠️ 警告" : "ℹ️ 提示";
    lines.push(
      `| ${levelLabel} | ${m.field ?? "—"} | ${m.message} | ${m.suggestion ?? "—"} |`,
    );
  }
  return lines.join("\n");
}

export function formatTotalSection(
  result: CalculationResult,
  heading = "## 六、宗地总地价",
): string {
  const lines: string[] = [
    heading,
    "",
    "| 项目 | 值 |",
    "|---|---:|",
    `| 宗地总地价 (元) | ${formatNumber(result.totalLandPrice)} |`,
    `| 计价建筑面积 (㎡) | ${result.totalChargeableGfa} |`,
    `| 综合楼面地价 (元/㎡) | ${formatNumber(result.averageFloorLandPrice)} |`,
    `| 分项数量 | ${result.itemResults.length} |`,
  ];
  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Top-level report composition
// ---------------------------------------------------------------------------

export function formatNewSupplyResult(
  parcel: Parcel,
  prices: MarketPriceSet,
  result: CalculationResult,
): string {
  return [
    "# 地价测算结果",
    "",
    `> 测算类型：新供应土地　·　项目：${parcel.projectName}　·　时点：${parcel.calculationDate}`,
    "",
    formatParcelSection(parcel),
    "",
    formatMarketPriceSection(prices),
    "",
    formatMarketPriceSourceSection(prices),
    "",
    "## 三、建筑面积分项",
    "",
    formatItemTable(result.itemResults),
    "",
    formatFactorChainSection(result),
    formatItemResultsSection(result),
    formatTotalSection(result),
    formatValidationSection(result.validationMessages),
  ].join("\n");
}

export function formatChangePremiumResult(
  result: ChangePremiumResult,
  projectName?: string,
): string {
  const name = projectName ?? result.beforeResult.itemResults[0]?.buildingType ?? "—";
  return [
    "# 变更补缴测算结果",
    "",
    `> 项目：${name} 宗地变更补缴`,
    "",
    "## 一、变更前宗地基础信息",
    "",
    formatParcelMini(result.beforeResult),
    "",
    "## 二、变更后宗地基础信息",
    "",
    formatParcelMini(result.afterResult),
    "",
    "## 三、变更前分项测算结果",
    "",
    formatItemResultsSection(result.beforeResult, "### 变更前分项"),
    "",
    "## 四、变更后分项测算结果",
    "",
    formatItemResultsSection(result.afterResult, "### 变更后分项"),
    "",
    "## 五、变更前后系数链（节选首项）",
    "",
    formatFactorChainSection(result.beforeResult, "### 变更前首项系数链"),
    "",
    formatFactorChainSection(result.afterResult, "### 变更后首项系数链"),
    "",
    "## 六、差额与应补缴地价",
    "",
    "| 项目 | 金额 (元) |",
    "|---|---:|",
    `| 变更前地价 | ${formatNumber(result.beforeResult.totalLandPrice)} |`,
    `| 变更后地价 | ${formatNumber(result.afterResult.totalLandPrice)} |`,
    `| 差额 | ${formatNumber(result.difference)} |`,
    `| **应补缴地价** | **${formatNumber(result.premium)}** |`,
    result.note ? `\n> ${result.note}\n` : "",
    "## 七、规则校验与人工判断项",
    "",
    formatValidationSection(
      [
        ...result.beforeResult.validationMessages,
        ...result.afterResult.validationMessages,
      ],
      "### 全部校验消息",
    ),
  ].join("\n");
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function formatItemTable(
  items: CalculationResult["itemResults"],
): string {
  if (items.length === 0) return "（无建筑面积分项）";
  const lines: string[] = [
    "| 分项ID | 建筑类型 | 建筑面积 (㎡) | 年期 | 产权条件 | 是否地下 | 是否政府产权 | 商业楼层 |",
    "|---|---|---:|---:|---|---|---|---|",
  ];
  for (const r of items) {
    lines.push(
      `| ${r.itemId} | ${r.buildingType} | ${r.gfa} | — | — | — | — | — |`,
    );
  }
  return lines.join("\n");
}

function formatParcelMini(result: CalculationResult): string {
  // We don't carry Parcel through ChangePremiumResult, so we show
  // totals + first-item info as a compact summary.
  return [
    "| 项目 | 值 |",
    "|---|---:|",
    `| 分项数量 | ${result.itemResults.length} |`,
    `| 计价建筑面积 (㎡) | ${result.totalChargeableGfa} |`,
    `| 宗地地价 (元) | ${formatNumber(result.totalLandPrice)} |`,
    `| 综合楼面地价 (元/㎡) | ${formatNumber(result.averageFloorLandPrice)} |`,
  ].join("\n");
}

// ---------------------------------------------------------------------------
// Top-level reports for the new modules
// ---------------------------------------------------------------------------

export function formatExcessGfaResult(result: ExcessGfaResult): string {
  return [
    "# 竣工超面积测算结果",
    "",
    `> 项目：${result.base.beforeResult.itemResults[0]?.buildingType ?? "—"} 宗地竣工超面积`,
    "",
    "## 一、底层变更补缴",
    "",
    formatChangePremiumResult(result.base, "（竣工超面积）"),
    "",
    "## 二、竣工超面积计收（130%）",
    "",
    "| 项目 | 金额 (元) |",
    "|---|---:|",
    `| 底层变更补缴应补地价 | ${formatNumber(result.base.premium)} |`,
    `| 竣工超面积应补缴地价（× 130%） | **${formatNumber(result.excessGfaPremium)}** |`,
    "",
    result.note ? `> ${result.note}` : "",
  ].join("\n");
}

export function formatTemporaryUseResult(
  result: TemporaryUseFeeResult,
  title: string,
  kindLabel: "临时用地使用费" | "短期租赁",
): string {
  return [
    `# ${kindLabel}测算结果`,
    "",
    `> 项目：${result.baseResult.itemResults[0]?.buildingType ?? "—"}（${title}）`,
    "",
    "## 一、宗地基础信息（来自底层新供应测算）",
    "",
    formatParcelMini(result.baseResult),
    "",
    "## 二、底层分项系数链",
    "",
    formatFactorChainSection(result.baseResult),
    "",
    "## 三、底层分项测算结果",
    "",
    formatItemResultsSection(result.baseResult),
    "",
    "## 四、应付费用",
    "",
    "| 项目 | 金额 (元) |",
    "|---|---:|",
    `| 应付${kindLabel} | **${formatNumber(result.totalFee)}** |`,
    "",
    result.note ? `> ${result.note}` : "",
  ].join("\n");
}

export function formatLeaseRentResult(
  result: KeyIndustrialLeaseAnnualRentResult,
): string {
  return [
    "# 重点产业项目租赁用地年租金测算结果",
    "",
    `> 项目：${result.baseResult.itemResults[0]?.buildingType ?? "—"}（20 年期地价 × 3%）`,
    "",
    "## 一、宗地基础信息（来自底层新供应测算）",
    "",
    formatParcelMini(result.baseResult),
    "",
    "## 二、底层分项系数链",
    "",
    formatFactorChainSection(result.baseResult),
    "",
    "## 三、底层分项测算结果",
    "",
    formatItemResultsSection(result.baseResult),
    "",
    "## 四、应付年租金",
    "",
    "| 项目 | 金额 (元) |",
    "|---|---:|",
    `| 20 年期宗地地价 | ${formatNumber(result.twentyYearLandPrice)} |`,
    `| 年租金（× 3%） | **${formatNumber(result.annualRent)}** |`,
    "",
    result.note ? `> ${result.note}` : "",
  ].join("\n");
}

export function formatCapacityIncreaseResult(
  result: IndustrialCapacityIncreaseResult,
): string {
  return [
    "# 产业用地提高容积测算结果",
    "",
    `> 项目：${result.base.beforeResult.itemResults[0]?.buildingType ?? "—"} 宗地提高容积`,
    "",
    "## 一、底层变更补缴（变更前 / 变更后）",
    "",
    formatChangePremiumResult(result.base),
    "",
    "## 二、容积提高按比例计收",
    "",
    "| 分项 ID | 底层应补地价 (元) | 调整后应补地价 (元) | 配套类型 |",
    "|---|---:|---:|---|",
  ]
    .concat(
      result.itemResults.map((r) =>
        [
          `| ${r.itemId}`,
          formatNumber(r.basePremium),
          formatNumber(r.adjustedPremium),
          r.supportingFacilityType ?? "—",
          "|",
        ].join(" "),
      ),
    )
    .concat([
      "",
      `> 容积提高应补地价合计：**${formatNumber(result.capacityIncreasePremium)} 元**`,
      "",
      "## 三、规则校验",
      "",
      formatValidationSection(
        result.base.afterResult.validationMessages,
        "### 校验消息",
      ),
    ])
    .join("\n");
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  // Round to 6 dp to keep the report readable while preserving precision.
  const rounded = Math.round(n * 1_000_000) / 1_000_000;
  return String(rounded);
}
